import { Directive } from 'vue'

type TouchInfo = {
    x: number
    y: number
}

interface IState {
    scale: number,
    touchDistance: number,
    startX: number,
    startY: number,

    dragPosition: TouchInfo,
    touchOffset: TouchInfo,
}

const prepareTouches = (e: TouchEvent): TouchInfo[] => {
  return Array.from(e.targetTouches).map(t => ({
    x: t.clientX,
    y: t.clientY,
  }))
}

const calcTouchDistance = (touches: Array<TouchInfo>): number => {
  return Math.hypot((touches[1]?.y ?? 0) - (touches[0]?.y ?? 0), (touches[1]?.x ?? 0) - (touches[0]?.x ?? 0))
}

export const vTouchResize: Directive = {
  mounted (el: HTMLElement) {
    el.style.touchAction = 'none'

    const state: IState = {
      scale: 1,
      touchDistance: 0,
      startX: el.offsetLeft,
      startY: el.offsetTop,
      dragPosition: {
        x: 0,
        y: 0,
      },
      touchOffset: {
        x: 0,
        y: 0,
      },
    }

    const updateDragPosition = ({ x, y }: TouchInfo) => {
      state.dragPosition.x = x - state.touchOffset.x
      state.dragPosition.y = y - state.touchOffset.y
    }

    const handleTouchStart = (event: TouchEvent) => {
      event.preventDefault()
      const touches = prepareTouches(event)

      if (touches.length > 1) {
        state.touchDistance = calcTouchDistance(touches)

      } else if (touches.length === 1) {
        const { x, y } = touches[0] ?? { x: 0, y: 0 }
        state.touchOffset.x = x - state.dragPosition.x
        state.touchOffset.y = y - state.dragPosition.y
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      event.preventDefault()

      const touches = prepareTouches(event)
      if (touches.length > 1) {
        const touchDistancePrev = state.touchDistance
        state.touchDistance = calcTouchDistance(touches)
        const frameChangeRatio = state.touchDistance / touchDistancePrev
        state.scale *= frameChangeRatio

        updateDragPosition(touches[0] ?? { x: 0, y: 0 })
        updateTransform()
      } else if (event.touches.length === 1 && state.scale > 1) {
        updateDragPosition(touches[0] ?? { x: 0, y: 0 })
        updateTransform()
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      event.preventDefault()
      const touches = prepareTouches(event)
      if (touches.length < 2) {
        state.touchDistance = 0
        if (state.scale < 1) {
          state.scale = 1
          state.dragPosition.x = 0 - state.startX
          state.dragPosition.y = 0 - state.startY
          updateTransform()
        }
      }
    }

    const updateTransform = () => {
      el.style.transform = `translate(${state.dragPosition.x}px, ${state.dragPosition.y}px) scale(${state.scale})`
    }

    el.addEventListener('touchstart', handleTouchStart)
    el.addEventListener('touchmove', handleTouchMove)
    el.addEventListener('touchend', handleTouchEnd)
  },
}
